"use client";

import { useCallback, useMemo } from "react";
import type { UseChatHelpers } from "@ai-sdk/react";
import type { NoxusUIMessage } from "../noxus-message-types";
import {
  QUESTION_PART_TYPE,
  QUESTION_TOOL_NAME,
  type Question,
  type SubmittedResponse,
  parseQuestions,
} from "./questionTypes";

export type PendingQuestionCall = {
  toolCallId: string;
  questions: Question[];
};

// The question calls waiting for the user, in the order the model made them.
// Only the last message can hold one: the SDK writes a tool output into the
// last message, and the backend only resumes the step that paused last.
export function getPendingQuestionCalls(
  messages: NoxusUIMessage[]
): PendingQuestionCall[] {
  const last = messages.at(-1);
  if (last?.role !== "assistant") return [];
  const pending: PendingQuestionCall[] = [];
  for (const part of last.parts) {
    if (part.type !== QUESTION_PART_TYPE) continue;
    if (!("state" in part) || part.state !== "input-available") continue;
    const questions = parseQuestions(part.input);
    if (questions) pending.push({ toolCallId: part.toolCallId, questions });
  }
  return pending;
}

type ChatHandle = Pick<
  UseChatHelpers<NoxusUIMessage>,
  "messages" | "addToolOutput"
>;

export type ActiveQuestions = {
  call: PendingQuestionCall;
  // Calls still waiting after this one, so the card can say "1 of 2".
  remaining: number;
  answer: (response: SubmittedResponse) => Promise<void>;
};

// The first unanswered question call, with what answers it. Several calls in
// one step are put to the user one after another; the run continues once every
// tool of the step has its output.
export function useQuestions(chat: ChatHandle): ActiveQuestions | null {
  const { messages, addToolOutput } = chat;
  const pending = useMemo(() => getPendingQuestionCalls(messages), [messages]);
  const call = pending[0];
  const toolCallId = call?.toolCallId;

  const answer = useCallback(
    (response: SubmittedResponse) => {
      if (!toolCallId) return Promise.resolve();
      return Promise.resolve(
        addToolOutput({
          tool: QUESTION_TOOL_NAME,
          toolCallId,
          output: response,
        })
      );
    },
    [toolCallId, addToolOutput]
  );

  if (!call) return null;
  return { call, remaining: pending.length - 1, answer };
}
